import type { ComponentType, SVGProps } from 'react';
import { Link, useLocation } from 'react-router';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { visaoAtiva, visoesPara, type IconeVisao, type PublicoVisao } from '../lib/visoes.js';
import { IconeAlerta, IconeAlvo, IconeBroto, IconeEtiqueta, IconeRelogio, IconeSelo } from './Icones.js';
import { SombrasDaFaixa, classeFaixaRolavel, useFaixaRolavel } from './ui/FaixaRolavel.js';

const ICONES: Record<IconeVisao, ComponentType<SVGProps<SVGSVGElement>>> = {
  relogio: IconeRelogio,
  selo: IconeSelo,
  alerta: IconeAlerta,
  alvo: IconeAlvo,
  broto: IconeBroto,
  etiqueta: IconeEtiqueta,
};

/**
 * Quem esta usando, para escolher as visoes. Mesma regra da navegacao (Layout):
 * a equipe cadastra; o dono do haras so consulta o plantel; com dados
 * ficticios, o avaliador sem cadastro consulta Plantel e Dados.
 */
export function usePublicoVisao(): PublicoVisao {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const usuario = useConsulta((f) => f.usuarioAtual(), []);
  const dono = usuario.estado === 'pronto' && usuario.dado?.papel === 'haras';
  if (equipe) return 'equipe';
  return dono || !fonte.ficticia ? 'dono' : 'consulta';
}

/** Lista vertical, na barra lateral escura (desktop). */
export function VisoesDaLateral({ publico }: { publico: PublicoVisao }) {
  const local = useLocation();
  return (
    <nav aria-label="Visões rápidas" className="flex flex-col gap-0.5">
      {visoesPara(publico).map((v) => {
        const Icone = ICONES[v.icone];
        const ativa = visaoAtiva(v, local);
        return (
          <Link
            key={v.id}
            to={v.para}
            title={v.descricao}
            aria-current={ativa ? 'page' : undefined}
            className={`flex min-h-10 items-center gap-3 rounded-(--radius-controle) px-3 text-[13.5px] font-medium transition-colors ${
              ativa
                ? 'bg-lateral-ativo text-sobre-lateral shadow-[inset_3px_0_0_var(--color-nevoa)]'
                : 'text-sobre-lateral/80 hover:bg-lateral-hover hover:text-sobre-lateral'
            }`}
          >
            <Icone width={18} height={18} className="shrink-0 opacity-90" />
            <span className="truncate">{v.rotulo}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Fileira de chips no topo das paginas, so abaixo de `lg` (onde nao ha barra
 * lateral). Rola de lado no celular, com degrade na borda quando ha mais.
 */
export function ChipsDeVisoes() {
  const publico = usePublicoVisao();
  const local = useLocation();
  const visoes = visoesPara(publico);
  const ativa = visoes.find((v) => visaoAtiva(v, local))?.id ?? null;
  const { ref, resta } = useFaixaRolavel<HTMLDivElement>(ativa, '[aria-current="page"]');
  return (
    <nav aria-label="Visões rápidas" className="relative -mx-4 lg:hidden">
      <div ref={ref} className={`${classeFaixaRolavel} gap-2 px-4 py-0.5`}>
        {visoes.map((v) => {
          const Icone = ICONES[v.icone];
          const atual = v.id === ativa;
          return (
            <Link
              key={v.id}
              to={v.para}
              title={v.descricao}
              aria-current={atual ? 'page' : undefined}
              className={`flex min-h-9 shrink-0 items-center gap-2 rounded-(--radius-chip) pointer-coarse:min-h-11 border px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors ${
                atual
                  ? 'border-marca bg-marca text-sobre-marca'
                  : 'border-borda bg-superficie text-texto hover:border-borda-forte'
              }`}
            >
              <Icone width={16} height={16} className="shrink-0 opacity-80" />
              {v.rotulo}
            </Link>
          );
        })}
      </div>
      <SombrasDaFaixa resta={resta} />
    </nav>
  );
}
